import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { BasicDialogBody, Dialog } from '../../../legacy-design-system/modal'
import { FormattedMessage, injectIntl, IntlShape, useIntl } from 'react-intl'
import gql from 'graphql-tag'
import { Mutation } from 'react-apollo'
import { MutationProps } from './merge-action'
import { SingleSelectComboboxObject } from '../../../legacy-design-system/combobox'
import { logError, logSuccess } from '../../toaster'
import { hasRole } from 'contrastBase/RoleSwitch'
import { Role } from '@contrast-security-inc/base-types'

interface Application {
  readonly id: string
  readonly name: string
  readonly role: Role
}

interface MergeDialogProps extends RenderDialogProps {
  readonly organizationID: string
  readonly applications: Application[]
}

interface State {
  readonly selectedApplication: Application
}

export class ConfirmMergeAppDialogInner extends React.Component<
  MergeDialogProps & {
    readonly intl: IntlShape
    readonly merge: (primaryID: string) => Promise<any>
  },
  State
> {
  constructor(
    props: MergeDialogProps & {
      readonly intl: IntlShape
      readonly merge: (primaryID: string) => Promise<any>
    }
  ) {
    super(props)

    if (props.applications.length <= 1) {
      throw new Error('There must be at least two applications for merging.')
    }

    this.state = {
      selectedApplication:
        props.applications.find(({ role }) => hasRole(role, 'EDIT')) ||
        props.applications[0]
    }
  }

  public render() {
    const { applications, merge, isOpen, closeDialog, intl } = this.props
    const { selectedApplication } = this.state

    return (
      <Dialog
        isOpen={isOpen}
        onCancel={closeDialog}
        renderTitle={() => <FormattedMessage id="title_mergeApplications" />}
      >
        <BasicDialogBody
          confirmButtonText={<FormattedMessage id="action_merge" />}
          cancelButtonText={<FormattedMessage id="action_cancel" />}
          disableConfirmButton={!hasRole(selectedApplication.role, 'EDIT')}
          onCancel={closeDialog}
          onConfirm={async () => {
            closeDialog()
            await merge(selectedApplication.id)
          }}
          body={
            <React.Fragment>
              <FormattedMessage id="detail_mergeApplications_mergeApps">
                {(message) => <p>{message}</p>}
              </FormattedMessage>
              <FormattedMessage id="detail_mergeApplication_selectRepresentativeApp">
                {(message) => <p>{message}</p>}
              </FormattedMessage>
              <SingleSelectComboboxObject<Application>
                label={intl.formatMessage({
                  id: 'detail_applicationsAvailableToMerge'
                })}
                renderLabel={() => undefined}
                items={[
                  {
                    items: applications.filter(({ role }) =>
                      hasRole(role, 'EDIT')
                    )
                  }
                ]}
                value={selectedApplication}
                itemToString={({ name }) => name}
                itemToID={({ id }) => id}
                onChange={this.onSelectedApplicationChange}
              />
            </React.Fragment>
          }
        />
      </Dialog>
    )
  }

  private onSelectedApplicationChange = (application: Application) => {
    this.setState({ selectedApplication: application })
  }
}

export function ConfirmMergeAppDialog(props) {
  const Wrapped = injectIntl(ConfirmMergeAppDialogInner)
  return <Wrapped {...props} />
}

const mergeApplications = gql`
  mutation MergeApplications(
    $organizationID: ID!
    $ids: [ID!]!
    $primaryID: ID!
  ) {
    mergeApplications(
      input: {
        organizationID: $organizationID
        ids: $ids
        primaryID: $primaryID
      }
    ) {
      _placeholder
    }
  }
`

export function MergeApplicationsDialog({
  organizationID,
  refresh,
  refetchQueries,
  applications,
  ...props
}: MergeDialogProps & MutationProps) {
  const { formatMessage } = useIntl()

  return (
    <Mutation mutation={mergeApplications}>
      {(mergeApplications) => {
        return (
          <ConfirmMergeAppDialog
            {...props}
            applications={applications}
            organizationID={organizationID}
            merge={(primaryID: string) => {
              return mergeApplications({
                refetchQueries,
                variables: {
                  organizationID,
                  primaryID,
                  ids: applications
                    .map((application) => application.id)
                    .filter((id) => id !== primaryID)
                },
                optimisticResponse: {
                  mergeApplications: {
                    _placeholder: true,
                    __typename: 'ApplicationBulkResponse'
                  },
                  __typename: 'Mutation'
                }
              })
                .then(async () => {
                  await refresh()
                  logSuccess(
                    formatMessage({
                      id: 'detail_mergeApplications_successfullyMergedApps'
                    })
                  )
                })
                .catch((error: Error) => {
                  logError(
                    formatMessage({
                      id: 'detail_mergeApplications_errorMergingApps'
                    })
                  )
                  return error
                })
            }}
          />
        )
      }}
    </Mutation>
  )
}

const mergeApplicationsByFilters = gql`
  mutation MergeApplicationsByFilters(
    $organizationID: ID!
    $primaryID: ID!
    $excludedApplications: [ID]!
    $filters: ApplicationFilterArg
  ) {
    mergeApplicationsByFilters(
      input: {
        organizationID: $organizationID
        primaryID: $primaryID
        excludedApplications: $excludedApplications
        filters: $filters
      }
    ) {
      _placeholder
    }
  }
`

interface BulkWithFiltersProps {
  readonly excludedApplications: string[]
  readonly filters: any
}

export function MergeApplicationsByFiltersDialog({
  organizationID,
  excludedApplications,
  filters,
  refresh,
  refetchQueries,
  ...props
}: MergeDialogProps & BulkWithFiltersProps & MutationProps) {
  return (
    <Mutation mutation={mergeApplicationsByFilters}>
      {(mergeApplicationsByFilters) => {
        return (
          <ConfirmMergeAppDialog
            {...props}
            organizationID={organizationID}
            merge={(primaryID: string) => {
              return mergeApplicationsByFilters({
                refetchQueries,
                variables: {
                  organizationID,
                  primaryID,
                  excludedApplications,
                  filters
                },
                optimisticResponse: {
                  mergeApplicationsByFilters: {
                    _placeholder: true,
                    __typename: 'ApplicationBulkResponse'
                  },
                  __typename: 'Mutation'
                }
              }).then(refresh)
            }}
          />
        )
      }}
    </Mutation>
  )
}
